import { Component, EventEmitter, Input, OnInit, DoCheck, Output, ViewChild, SimpleChanges } from '@angular/core';
import { XsI18nPipe } from '@nx-fe/pipes';
import {
  DeviceContextService,
  DeviceService,
  FeatureToggleService,
  GlobalEventBusService,
  hd,
  LoggerService,
  TicketContextService,
} from '@nx-fe/services';
import { AttachmentService } from 'app/modules/ticket/services/attachment.service';
import { DeviceCaptureActivityTypes } from 'app/modules/ticket/ticket-page.service';
import { ActivityTemplateBindings } from '../../activity/activity-context';
import {
  ActivityTypeEnum,
  Activity,
  AttachmentActivity,
  DeviceCaptureActivity,
  UploadedFile,
  isAttachmentActivity,
  isDeviceCaptureActivity,
  isDeviceSnapshotActivity,
} from '../../../../ticket.model';
import { IFileInfo } from 'app/modules/remote-control/file-manager/fileData';
import { FileSaveParams, PromptDialogComponent } from 'xsshared/components/prompt-dialog/prompt-dialog.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import * as moment from 'moment';
import { FileSizeFormatPipe } from '../../../../../../../../../../libs/pipes/src/lib/file-size-format.pipe';

type IDeviceCaptureActivity = hd.IDeviceCaptureActivity;
type ISnapshotActivity = hd.ISnapshotActivity;
type IAttachmentActivity = hd.IAttachmentActivity;

export interface IExistingAttachmentActivityBindings extends ActivityTemplateBindings {
  /** Callback invoke to delete the attachment */
  onDelete: string | Function;
  /** Callback to invoke when an error occurs */
  onError: string | Function;
}

export interface ActivityDisplayInformation extends Activity {
  activityTypeCaption: string;

  activityProcurementCaption: string;

  activityCaption: string;

  activityInformation: string[];

  activityThumbnail: string;
}

/** Component for an existing attachment activity */
@Component({
  selector: 'xs-existing-attachment-activity',
  templateUrl: './existing-attachment-activity.component.html',
  styleUrls: ['./existing-attachment-activity.component.scss'],
})
export class ExistingAttachmentActivityComponent implements OnInit, DoCheck {
  /** The parent activity, set during controller initialization */
  @Input() parentActivity: Activity;

  @Input()
  public canDelete: boolean;

  /** Indicates whether the note activity is editable or not */
  @Input() canEdit: boolean;

  /** Indicates that we are calling that component in Self Service Portal */
  @Input()
  public isSPPMode: boolean;

  /** The ActivityController exposes methods for saving, deleting, and updating activities */
  @Input() activityCtrl: any;

  /** Callback invoke to delete the attachment */
  @Output() public onDelete: EventEmitter<any> = new EventEmitter();

  /** Callback to invoke when an error occurs  */
  @Output() public onError: EventEmitter<any> = new EventEmitter();

  @Input() displayInformation: ActivityDisplayInformation;

  @ViewChild('popupDelete', { static: false }) popupDelete;

  private _fileSize = 0;
  private _checkFileSize: boolean = false;

  /** Device Name */
  public deviceName: string;

  public isMediaReady: boolean;

  constructor(
    private logger: LoggerService,
    private attachmentService: AttachmentService,
    private xsI18nPipe: XsI18nPipe,
    private featureToggleService: FeatureToggleService,
    private ticketContextService: TicketContextService,
    private fileSizeFormatPipe: FileSizeFormatPipe,
    private dialog: MatDialog
  ) {}

  private _isDefaultConnection: boolean = null;

  /**
   * Called to pass required information during controller initialization.
   * @param activity A new or existing attachment activity.
   * @param file Optional file selected to upload (for new attachments).
   */
  ngOnInit(): void {
    if (isAttachmentActivity(this.parentActivity)) {
      this.displayInformation =
        this.createAttachmentDisplayInformation(this.parentActivity) ||
        (Object.assign({}, <unknown>this.parentActivity) as ActivityDisplayInformation);
      this.isMediaReady = true;
      if (this.parentActivity.displayName) {
        this.deviceName = this.parentActivity.displayName;
      }
    } else if (isDeviceCaptureActivity(this.parentActivity)) {
      //this.subscribeOnceOnUploadComplete(this.parentActivity);
      this._checkFileSize =
        !this.parentActivity.details.files?.length || this.parentActivity.details.files[0].fileSize == null;
      this.isMediaReady = !this._checkFileSize;
      this.displayInformation = this.createDeviceCaptureAttachmentDisplayInformation(this.parentActivity);
    }
  }

  ngDoCheck(): void {
    if (this._checkFileSize && this.parentActivity.details.files?.length > 0) {
      if (this._fileSize !== this.parentActivity.details.files[0].fileSize) {
        this._fileSize = this.parentActivity.details.files[0].fileSize;
        this.isMediaReady = true;
        this._checkFileSize = false;
        this.displayInformation = this.createDeviceCaptureAttachmentDisplayInformation(this.parentActivity);
      }
    } else if (this._isDefaultConnection == null && isDeviceCaptureActivity(this.parentActivity)) {
      // isDefaultConnection gets assigned on the server, so the locally created activity doesn't have it.
      // Wait until this is set by checking for null or undefined and recreate displayInformation when done
      this.displayInformation = this.createDeviceCaptureAttachmentDisplayInformation(this.parentActivity);
    }
  }

  /** Initiates a preview of the attachment */
  public previewOrDownload(): void {
    const currentPreviewAttachment = this.attachmentService.getPreviewAttachment();
    if (!currentPreviewAttachment || currentPreviewAttachment.activityId !== this.parentActivity.activityId) {
      if (!this.attachmentService.setPreviewAttachment(this.parentActivity)) {
        // If the attachment can not be previewed (for example, because it's not a supported preview file type),
        // then just trigger a download
        this.downloadFile();
      }
    }
  }

  /** Opens the download file dialog */
  public downloadFile = (): void => {
    let extension: string;
    if (this.isActivityTypeAdbLogs(this.parentActivity)) {
      extension = '.zip';
    }

    // TODO - models, passing fake IAttacmentActivity
    const activity = Object.assign({}, this.parentActivity, {
      activityType: 'attachment',
      fileName: this.getFileName(),
      fileId: this.parentActivity.details?.fileId || this.parentActivity.details?.files?.[0].fileId,
    });
    const modalSettings = this.attachmentService.createDownloadPromptConfig(
      (<unknown>activity) as AttachmentActivity,
      extension
    );
    const input: hd.PromptDialogParams = modalSettings.resolve.dialogParams;

    const fileSaveParams: hd.FileSaveParams = {
      fileName: activity.fileName,
      extension: input.extension,
    };

    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '394px';
    dialogConfig.panelClass = 'custom-dialog-container';
    dialogConfig.closeOnNavigation = true;
    dialogConfig.data = {
      title: input.title,
      icon: input.icon,
      confirmButton: input.confirmButton,
      inputTitle: input.inputTitle,
      extension: input.extension,
      placeHolder: this.downloadNameUtcToLocalTime(this.parentActivity, input.placeHolder),
      regexFilter: input.regexFilter,
      unallowedChar: input.unallowedChar,
      fileParams: fileSaveParams,
    };

    const modalInstance = this.dialog.open(PromptDialogComponent, dialogConfig);
    modalInstance.afterClosed().subscribe((result: FileSaveParams) => {
      if (result) {
        this.attachmentService.createDownloadWithUserInput(this.parentActivity, result).subscribe(
          (downloadUrl: string) => {
            // trigger file download
            const fileInfo: IFileInfo = {
              fileName: fileSaveParams.fileName + fileSaveParams.extension,
              filePath: downloadUrl,
            };
            this.attachmentService
              .downloadFile(fileInfo)
              .then(() => {
                console.log('download succeeded');
              })
              .catch(err => {
                this.logger.debug('Failed to download');
                this.processDownloadError(err);
              });
          },
          (err: hd.IError) => {
            this.logger.debug('Failed to create download');
            this.processDownloadError(err);
          }
        );
      }
    });
  };

  private processDownloadError = (err: any) => {
    let message: string;
    if (err.status && err.status === 404) {
      message = this.xsI18nPipe.transform('attachment_AttachmentNotFound_Error');
    } else {
      message = this.xsI18nPipe.transform('attachment_DownloadFailed_Error');
    }
    this.onError.emit({ error: message, canRetry: false });
    err.isHandled = true;
  };

  public downloadFileFromSsp = () => {
    // Clone the activity
    const activity = <AttachmentActivity>{};
    if (this.parentActivity && this.isActivityTypeAdbLogs(this.parentActivity)) {
      activity.activityId = this.parentActivity.parentActivityId;
      activity.fileName = 'logs.zip';
    } else {
      // angular.copy(this.activity, activity);
      activity.fileName = activity.displayName !== null ? activity.displayName : activity.fileName;
    }

    // Create the download
    this.attachmentService.createDownload(activity).then(
      (downloadUrl: string) => {
        // Broadcast downloadFile event. This will be handled by the downloadFile directive
        // this.$rootScope.$broadcast('downloadFile', downloadUrl);
      },
      (err: hd.IError) => {
        this.logger.debug('Failed to create download');
        let message: string;
        if (err.status && err.status === 404) {
          message = this.xsI18nPipe.transform('attachment_AttachmentNotFound_Error');
        } else {
          message = this.xsI18nPipe.transform('attachment_DownloadFailed_Error');
        }
        this.onError.emit({ error: message, canRetry: false });
        err.isHandled = true;
      }
    );
  };

  /** Should we display the delete button  */
  public showDeleteButton = (): boolean => {
    // canDelete defined in attachmentActivity template
    if (this.isFeatureToggleEnabled('AbacAllowableActionsGroups')) {
      return this.ticketContextService.canDeleteNote();
    } else {
      return this.canEdit;
    }
  };

  private getFileName = (): string => {
    const activity: any = this.parentActivity;
    if (isAttachmentActivity(activity)) {
      if (
        activity.details?.files &&
        typeof activity.details.files === 'object' &&
        Object.keys(activity.details.files).includes('0')
      ) {
        return activity.details.files[0].fileName;
      }
    } else if (isDeviceCaptureActivity(activity)) {
      if (
        activity.details?.files &&
        typeof activity.details.files === 'object' &&
        Object.keys(activity.details.files).includes('0')
      ) {
        const fileNameAdditional =
          activity && activity.creationDate
            ? moment(new Date(activity.creationDate).toUTCString()).format(' YYYYMMDD[T]HH:mm:ss')
            : '';

        return activity.details.files.length == 1
          ? activity.details.files[0].fileName
          : `${
              activity.details.deviceName || activity.details.mcConnectionName || 'DeviceLogs'
            }${fileNameAdditional}.zip`;
      }
    }

    return activity.details?.fileName || activity.fileName || '';
  };

  public getCaption(): string {
    return this.displayInformation?.activityTypeCaption;
  }

  public showFileSize(): boolean {
    return (
      !isDeviceCaptureActivity(this.parentActivity) ||
      this.parentActivity.details?.captureType !== DeviceCaptureActivityTypes.adbLogs
    );
  }

  public showAttachmentMedia(): boolean {
    return this.parentActivity?.activityId && !isDeviceSnapshotActivity(this.parentActivity);
  }

  public showSnapshotThumbnail(): boolean {
    return this.displayInformation?.activityThumbnail !== null;
  }

  /** Whether the specified file can be previewed */
  public showPreviewButton(): boolean {
    if (this.isSPPMode) {
      return false;
    }

    if (this.parentActivity.details.captureType === DeviceCaptureActivityTypes.adbLogs) {
      return !this.parentActivity.details?.$$isDraft || this.isMediaReady;
    }
    return this.attachmentService.isFileTypePreviewSupported(this.getFileName());
  }

  public showDownloadButton(): boolean {
    if (this.ticketContextService.isLicenceExpired) {
      return false;
    }

    return (
      this.parentActivity &&
      !isDeviceSnapshotActivity(this.parentActivity) &&
      !(isDeviceCaptureActivity(this.parentActivity) && this.parentActivity.details?.$$isDraft)
    );
  }

  /** Whether the activity is of type Adb Logs */
  public isActivityTypeAdbLogs(activity: Activity): boolean {
    return this.parentActivity && this.parentActivity.activityTypeId === ActivityTypeEnum.deviceLog;
  }

  /**
   * Checks if a feature toggle is enabled
   */
  public isFeatureToggleEnabled(featureToggleId: string): boolean {
    return this.featureToggleService.isEnabled(featureToggleId);
  }

  public getDisplayInformation(): string {
    let result = '';

    if (this.displayInformation?.activityInformation) {
      const filtered = this.displayInformation.activityInformation.filter(item => !!item);
      result = filtered.join(', ');
    }

    return result;
  }

  private createAttachmentDisplayInformation = (attachmentActivity: AttachmentActivity): ActivityDisplayInformation => {
    // result for a regular attachment
    const result = {
      creationDate: attachmentActivity.creationDate,
      activityTypeCaption: 'attachment_Caption',
      activityProcurementCaption: 'attachment_UploadedBy_Caption',
      activityCaption: attachmentActivity.details.fileName,
      activityInformation: [this.fileSizeFormatPipe.transform(attachmentActivity.details.fileSize)],
    };

    return result as any;
  };

  private createDeviceCaptureAttachmentDisplayInformation = (
    deviceCaptureActivity: DeviceCaptureActivity
  ): ActivityDisplayInformation => {
    // TODO - model fixes
    this.attachmentService.patchActivity(deviceCaptureActivity);

    let mcConnectionName = deviceCaptureActivity.details.mcConnectionName;
    if (!mcConnectionName) {
      this._isDefaultConnection = this.parentActivity?.details.isDefaultConnection;
      mcConnectionName = this.xsI18nPipe.transform(
        this._isDefaultConnection
          ? 'associateDevices_Default_Instance_Caption'
          : 'associateDevices_Unknown_Instance_Caption'
      );
    } else {
      this._isDefaultConnection = false;
    }
    if (!deviceCaptureActivity.details.deviceName) {
      deviceCaptureActivity.details.deviceName = mcConnectionName;
    }

    return {
      creationDate: deviceCaptureActivity.creationDate || new Date(),
      activityTypeCaption: this.getAttachmentCaption(deviceCaptureActivity.details.captureType),
      activityProcurementCaption: 'attachment_CapturedBy_Caption',
      activityCaption: deviceCaptureActivity.details.deviceName,
      activityInformation:
        deviceCaptureActivity.details.captureType !== DeviceCaptureActivityTypes.adbLogs &&
        deviceCaptureActivity.details.files?.length > 0
          ? [mcConnectionName, this.fileSizeFormatPipe.transform(deviceCaptureActivity.details.files[0].fileSize)]
          : [mcConnectionName, ''] /* HD-11601 - ADB file size should not be displayed in the activity panel */,
    } as any;
  };

  private getAttachmentCaption = (deviceCaptureActivityType: DeviceCaptureActivityTypes): string => {
    let attachmentTypeCaption = '';

    switch (deviceCaptureActivityType) {
      case DeviceCaptureActivityTypes.video:
        attachmentTypeCaption = 'attachment_VideoRecording_Caption';
        break;
      case DeviceCaptureActivityTypes.audiovideo:
        attachmentTypeCaption = 'attachment_VideoRecording_With_Audio_Caption';
        break;
      case DeviceCaptureActivityTypes.image:
        attachmentTypeCaption = 'attachment_ScreenCapture_Caption';
        break;
      case DeviceCaptureActivityTypes.adbLogs:
        attachmentTypeCaption = 'attachment_DeviceLog_Caption';
        break;
      case DeviceCaptureActivityTypes.file:
        attachmentTypeCaption = 'attachment_Caption';
        break;
      case DeviceCaptureActivityTypes.snapshot:
        attachmentTypeCaption = 'snapshot_Caption';
        break;
    }

    return attachmentTypeCaption;
  };

  public getAttachmentTypeCaption(): string {
    return this.xsI18nPipe.transform(this.displayInformation.activityTypeCaption);
  }

  public deleteAttachment() {
    this.onDelete.emit();
    this.popupDelete.close();
  }

  public cancelDelete() {
    this.popupDelete.close();
  }

  // video capture files are uploaded with timestamped names, in order to avoid name collision
  // (video files do not have API assigned GUID names, inlike other uploaded files).
  // That timestamp is in UTC, and needs to be converted to local time
  private downloadNameUtcToLocalTime = (activity: Activity, fileName: string): string => {
    if (
      isDeviceCaptureActivity(activity) &&
      (DeviceCaptureActivityTypes.video === activity.details.captureType ||
        DeviceCaptureActivityTypes.audiovideo === activity.details.captureType)
    ) {
      const rTimestamp = /_20[2-9]\d[0-1]\d[0-3]\d_[0-2]\d[0-6]\d[0-6]\d$/;
      const timestamps = fileName.match(rTimestamp);
      if (timestamps && timestamps.length > 0) {
        const timestamp = timestamps.pop();
        const date = moment.utc(timestamp.slice(1), 'YYYYMMDD_HHmmss');
        if (date.isValid()) {
          const localTimestamp = date.local().format('YYYYMMDD_HHmmss');
          return fileName.slice(0, -timestamp.length) + '_' + localTimestamp;
        }
      }
    }
    return fileName;
  };
}
